import { useState, useCallback } from 'react';

/**
 * Custom hook for form validation and management
 * Provides reusable form state management with validation
 */
const useFormValidation = (initialState, validationRules) => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validate a single field
  const validateField = useCallback((name, value) => {
    const rule = validationRules[name];
    if (!rule) return '';

    if (rule.required && (!value || !value.toString().trim())) {
      return rule.requiredMessage || `${name} is required`;
    }

    if (rule.minLength && value.toString().length < rule.minLength) {
      return rule.minLengthMessage || `${name} must be at least ${rule.minLength} characters`;
    }

    if (rule.maxLength && value.toString().length > rule.maxLength) {
      return rule.maxLengthMessage || `${name} must be no more than ${rule.maxLength} characters`;
    }

    if (rule.pattern && !rule.pattern.test(value)) {
      return rule.patternMessage || `${name} format is invalid`;
    }

    if (rule.custom && typeof rule.custom === 'function') {
      return rule.custom(value) || '';
    }

    return '';
  }, [validationRules]);

  // Validate all fields
  const validateForm = useCallback(() => {
    const newErrors = {};
    let isValid = true;

    Object.keys(validationRules).forEach(fieldName => {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [formData, validateField, validationRules]);

  // Handle input change
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: fieldValue,
    }));

    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  }, [errors]);

  // Handle form submission
  const handleSubmit = useCallback((onSubmit) => {
    return async (e) => {
      e.preventDefault();
      
      if (isSubmitting) return;

      if (!validateForm()) {
        return false;
      }

      setIsSubmitting(true);
      
      try {
        await onSubmit(formData);
        return true;
      } catch (error) {
        console.error('Form submission error:', error);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    };
  }, [formData, isSubmitting, validateForm]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(initialState);
    setErrors({});
    setIsSubmitting(false);
  }, [initialState]);

  // Set form data programmatically
  const setFormField = useCallback((name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // Set multiple form fields
  const setFormFields = useCallback((fields) => {
    setFormData(prev => ({
      ...prev,
      ...fields,
    }));
  }, []);

  return {
    formData,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
    validateForm,
    resetForm,
    setFormField,
    setFormFields,
    setIsSubmitting,
    setErrors,
  };
};

export default useFormValidation;